import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ShoppingCart, 
  DollarSign, 
  Truck, 
  Users,
  TrendingUp,
  Calendar,
  Factory,
  AlertCircle,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ArrowRight,
  Scale
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchFinancialSummary();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/financial-summary`, {
        headers: getAuthHeader()
      });
      setFinancialSummary(response.data);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getDateLocale = () => {
    switch(language) {
      case 'de': return 'de-DE';
      case 'tr': return 'tr-TR';
      case 'en': return 'en-US';
      case 'ku': return 'ku';
      default: return 'ar-EG';
    }
  };

  const today = new Date().toLocaleDateString(getDateLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-stone-900">
          {t('welcome')}, {user?.name}
        </h1>
        <div className="flex items-center gap-2 text-stone-500 mt-1">
          <Calendar className="w-4 h-4" />
          <span>{today}</span>
        </div>
      </div>

      {/* Overdue Financial Dues Alert */}
      {financialSummary && financialSummary.total_overdue_count > 0 && (
        <Link to="/financial-dues" className="block">
          <Card className="border-red-300 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer" data-testid="overdue-alert">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-bold text-red-800">تنبيه: يوجد {financialSummary.total_overdue_count} مستحق متأخر!</p>
                    <p className="text-sm text-red-700">
                      {financialSummary.receivables.overdue_count > 0 && (
                        <span>لنا: {formatCurrency(financialSummary.receivables.overdue)} € ({financialSummary.receivables.overdue_count})</span>
                      )}
                      {financialSummary.receivables.overdue_count > 0 && financialSummary.payables.overdue_count > 0 && ' | '}
                      {financialSummary.payables.overdue_count > 0 && (
                        <span>علينا: {formatCurrency(financialSummary.payables.overdue)} € ({financialSummary.payables.overdue_count})</span>
                      )}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">{t('totalCustomers')}</p>
                <p className="text-xl md:text-2xl font-bold text-blue-700 mt-1">{stats?.total_customers || 0}</p>
              </div>
              <div className="w-10 h-10 bg-blue-200 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">{t('todayOrders')}</p>
                <p className="text-xl md:text-2xl font-bold text-amber-700 mt-1">{stats?.today_orders || 0}</p>
              </div>
              <div className="w-10 h-10 bg-amber-200 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">{t('todayRevenue')}</p>
                <p className="text-xl md:text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(stats?.today_revenue)} €</p>
              </div>
              <div className="w-10 h-10 bg-emerald-200 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 font-medium">{t('totalOutstanding') || 'المبالغ المستحقة'}</p>
                <p className="text-xl md:text-2xl font-bold text-red-700 mt-1">{formatCurrency(stats?.total_outstanding)} €</p>
              </div>
              <div className="w-10 h-10 bg-red-200 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Production + Payments Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Today's Production */}
        <Card className="border-stone-200" data-testid="production-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Factory className="w-5 h-5 text-amber-600" />
              {t('dailyProduction')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.production_items?.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {stats.production_items.map((item, idx) => (
                  <div key={idx} className="text-center p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <p className="text-2xl font-bold text-amber-700">{item.quantity}</p>
                    <p className="text-xs text-amber-600 mt-1">{item.product_name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-stone-400">
                <Factory className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('noProductionToday') || 'لم يتم تسجيل إنتاج اليوم'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card className="border-stone-200" data-testid="financial-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              {t('financialSummary') || 'الملخص المالي'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-emerald-700">{t('todayPayments') || 'مدفوعات اليوم'}</span>
              <span className="font-bold text-emerald-700">{formatCurrency(stats?.today_payments_total)} €</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-blue-700">{t('weeklyOrders')}</span>
              <span className="font-bold text-blue-700">{stats?.weekly_orders || 0} ({formatCurrency(stats?.week_revenue)} €)</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <span className="text-sm text-amber-700">{t('pendingDelivery')}</span>
              <span className="font-bold text-amber-700">{stats?.pending_deliveries || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
              <span className="text-sm font-bold text-red-700">{t('totalOutstanding') || 'إجمالي المبالغ المستحقة'}</span>
              <span className="font-bold text-red-700">{formatCurrency(stats?.total_outstanding)} €</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Sales (who bought) */}
      {stats?.today_sales?.length > 0 && (
        <Card className="border-stone-200" data-testid="today-sales-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              {t('todaySales') || 'مبيعات اليوم - لمن بيعت'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-100">
                    <th className="p-2 text-right font-semibold">{t('customerName')}</th>
                    <th className="p-2 text-center font-semibold">{t('total')}</th>
                    <th className="p-2 text-center font-semibold">{t('deliveryStatus') || 'حالة التسليم'}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.today_sales.map((sale, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      <td className="p-2 font-medium">{sale.customer_name}</td>
                      <td className="p-2 text-center font-bold text-emerald-600">{formatCurrency(sale.total)} €</td>
                      <td className="p-2 text-center">
                        {sale.delivered ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> {t('delivered') || 'تم التسليم'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            <Clock className="w-3 h-3" /> {t('pending') || 'بانتظار'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base">{t('recentOrders')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_orders.slice(0, 5).map((order, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm text-stone-900">{order.customer_name}</p>
                      <p className="text-xs text-stone-500">{order.date}</p>
                    </div>
                    <p className="font-bold text-sm text-emerald-600">{formatCurrency(order.total)} €</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-4 text-sm">{t('noOrders')}</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base">{t('topCustomers')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.top_customers?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_customers.slice(0, 5).map((customer, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                      <p className="font-medium text-sm text-stone-900">{customer.name}</p>
                    </div>
                    <p className="font-bold text-sm text-emerald-600">{formatCurrency(customer.total)} €</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-4 text-sm">{t('noCustomers')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
